import React from 'react';

import { cn } from '@/lib/utils';

// Type for components with displayName
interface ComponentWithDisplayName {
  displayName?: string;
}

// Type for element props with className
interface PropsWithClassName {
  className?: string;
  children?: React.ReactNode;
}

interface ActionButtonGroupProps {
  children: React.ReactNode;
  className?: string;
}

export function ActionButtonGroup({
  children,
  className,
}: ActionButtonGroupProps) {
  // Filter out falsy children (false, null, undefined) before counting
  const validChildren = React.Children.toArray(children).filter(Boolean);
  const childCount = validChildren.length;

  // For single button, use flex layout that fills width on mobile
  if (childCount === 1) {
    return (
      <div className={cn('flex w-full sm:justify-end', className)}>
        {validChildren.map((child, _index) => {
          if (!React.isValidElement(child)) return child;

          // If it's a Link element, ensure both link and button fill width
          if (
            child.type === 'a' ||
            (child.type as ComponentWithDisplayName)?.displayName === 'Link'
          ) {
            const childProps = child.props as PropsWithClassName;
            const linkChildren = React.Children.map(
              childProps.children,
              buttonChild => {
                if (!React.isValidElement(buttonChild)) return buttonChild;
                const buttonProps = buttonChild.props as PropsWithClassName;

                return React.cloneElement(
                  buttonChild as React.ReactElement<Record<string, unknown>>,
                  {
                    className: cn(
                      buttonProps.className?.replace('w-full sm:w-auto', ''),
                      'w-full sm:w-auto'
                    ),
                  }
                );
              }
            );

            return React.cloneElement(
              child as React.ReactElement<Record<string, unknown>>,
              {
                children: linkChildren,
                className: cn(childProps.className, 'w-full sm:w-auto block'),
              }
            );
          }

          // For direct buttons
          const childProps = child.props as PropsWithClassName;
          return React.cloneElement(
            child as React.ReactElement<Record<string, unknown>>,
            {
              className: cn(childProps.className, 'w-full sm:w-auto'),
            }
          );
        })}
      </div>
    );
  }

  // For 4 buttons, create a 2-row layout
  if (childCount === 4) {
    const childArray = validChildren;
    return (
      <div className={cn('flex flex-col gap-2 w-full sm:items-end', className)}>
        <div className="flex gap-2 w-full sm:w-auto">
          {childArray.slice(0, 2).map((child, index) => {
            if (!React.isValidElement(child)) return child;

            // Handle Link-wrapped buttons
            if (
              child.type === 'a' ||
              (child.type as ComponentWithDisplayName)?.displayName === 'Link'
            ) {
              const childProps = child.props as PropsWithClassName;
              const linkChildren = React.Children.map(
                childProps.children,
                buttonChild => {
                  if (!React.isValidElement(buttonChild)) return buttonChild;
                  const buttonProps = buttonChild.props as PropsWithClassName;
                  return React.cloneElement(
                    buttonChild as React.ReactElement<Record<string, unknown>>,
                    {
                      className: cn(buttonProps.className, 'w-full'),
                    }
                  );
                }
              );

              return React.cloneElement(
                child as React.ReactElement<Record<string, unknown>>,
                {
                  key: index,
                  children: linkChildren,
                  className: cn(childProps.className, 'flex-1'),
                }
              );
            }

            const childProps = child.props as PropsWithClassName;
            return React.cloneElement(
              child as React.ReactElement<Record<string, unknown>>,
              {
                key: index,
                className: cn(childProps.className, 'w-full flex-1'),
              }
            );
          })}
        </div>
        <div className="flex gap-2 w-full sm:w-auto">
          {childArray.slice(2, 4).map((child, index) => {
            if (!React.isValidElement(child)) return child;

            // Handle Link-wrapped buttons
            if (
              child.type === 'a' ||
              (child.type as ComponentWithDisplayName)?.displayName === 'Link'
            ) {
              const childProps = child.props as PropsWithClassName;
              const linkChildren = React.Children.map(
                childProps.children,
                buttonChild => {
                  if (!React.isValidElement(buttonChild)) return buttonChild;
                  const buttonProps = buttonChild.props as PropsWithClassName;
                  return React.cloneElement(
                    buttonChild as React.ReactElement<Record<string, unknown>>,
                    {
                      className: cn(buttonProps.className, 'w-full'),
                    }
                  );
                }
              );

              return React.cloneElement(
                child as React.ReactElement<Record<string, unknown>>,
                {
                  key: index + 2,
                  children: linkChildren,
                  className: cn(childProps.className, 'flex-1'),
                }
              );
            }

            const childProps = child.props as PropsWithClassName;
            return React.cloneElement(
              child as React.ReactElement<Record<string, unknown>>,
              {
                key: index + 2,
                className: cn(childProps.className, 'w-full flex-1'),
              }
            );
          })}
        </div>
      </div>
    );
  }

  // Two buttons: stacked full-width on phones, one right-aligned row on
  // desktop (the secondary sits to the left of the primary).
  if (childCount === 2) {
    return (
      <div
        className={cn(
          'flex flex-col gap-2 w-full sm:flex-row sm:justify-end sm:gap-3',
          className
        )}
      >
        {validChildren.map((child, index) => {
          if (!React.isValidElement(child)) return child;

          // If it's a Link element, ensure the button inside has proper width
          if (
            child.type === 'a' ||
            (child.type as ComponentWithDisplayName)?.displayName === 'Link'
          ) {
            const childProps = child.props as PropsWithClassName;
            const linkChildren = React.Children.map(
              childProps.children,
              buttonChild => {
                if (!React.isValidElement(buttonChild)) return buttonChild;
                const buttonProps = buttonChild.props as PropsWithClassName;

                return React.cloneElement(
                  buttonChild as React.ReactElement<Record<string, unknown>>,
                  {
                    className: cn(buttonProps.className, 'w-full sm:w-auto'),
                  }
                );
              }
            );

            return React.cloneElement(
              child as React.ReactElement<Record<string, unknown>>,
              {
                key: index,
                children: linkChildren,
                className: cn(childProps.className, 'w-full sm:w-auto'),
              }
            );
          }

          // For direct buttons
          const childProps = child.props as PropsWithClassName;
          return React.cloneElement(
            child as React.ReactElement<Record<string, unknown>>,
            {
              key: index,
              className: cn(childProps.className, 'w-full sm:w-auto'),
            }
          );
        })}
      </div>
    );
  }

  // For 3 buttons, use grid on mobile, flex on desktop
  return (
    <div
      className={cn(
        'grid grid-cols-2 gap-2 w-full',
        'sm:flex sm:flex-row sm:flex-nowrap sm:gap-2 sm:justify-end sm:w-auto',
        className
      )}
    >
      {validChildren.map((child, _index) => {
        if (!React.isValidElement(child)) return child;

        // If it's a Link element, ensure the button inside has proper width
        if (
          child.type === 'a' ||
          (child.type as ComponentWithDisplayName)?.displayName === 'Link'
        ) {
          const childProps = child.props as PropsWithClassName;
          const linkChildren = React.Children.map(
            childProps.children,
            buttonChild => {
              if (!React.isValidElement(buttonChild)) return buttonChild;
              const buttonProps = buttonChild.props as PropsWithClassName;

              return React.cloneElement(
                buttonChild as React.ReactElement<Record<string, unknown>>,
                {
                  className: cn(buttonProps.className, 'w-full sm:w-auto'),
                }
              );
            }
          );

          return React.cloneElement(
            child as React.ReactElement<Record<string, unknown>>,
            {
              children: linkChildren,
            }
          );
        }

        // For direct buttons
        const childProps = child.props as PropsWithClassName;
        return React.cloneElement(
          child as React.ReactElement<Record<string, unknown>>,
          {
            className: cn(childProps.className, 'w-full sm:w-auto'),
          }
        );
      })}
    </div>
  );
}
